import { baseApiClient } from './apiClient';

// Types
export interface BackgroundOption {
    text: Record<string, string>;
    priorShift: Record<string, number>;
    code?: string;
    icon?: string | null;
    courseCount?: number;
}

export interface BackgroundQuestion {
    id: string;
    question: Record<string, string>;
    weight: number;
    options: BackgroundOption[];
    type?: 'single-select' | 'multi-select';
    dynamic?: boolean;
    minSelections?: number;
}

export interface BackgroundAnswer {
    questionId: string;
    optionIndex: number;
    optionIndices?: number[];
    selectedTopicCodes?: string[];
}

export interface QuestionOption {
    id: number;
    text: string;
    textAudio?: string;
}

export interface TestQuestion {
    id: string;
    questionType: string;
    content: {
        stem: string;
        stemAudio?: string;
        media?: {
            type: 'image' | 'audio' | 'video';
            url: string;
        };
        options: QuestionOption[];
    };
    timing: {
        expectedTime: number;
        fastThreshold: number;
        slowThreshold: number;
        maxTime: number;
    };
}

export type CEFRLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export interface CEFRProbabilities {
    A1: number;
    A2: number;
    B1: number;
    B2: number;
    C1: number;
    C2: number;
}

export interface TestResult {
    primaryLevel: CEFRLevel;
    primaryProbability: number;
    secondaryLevel: CEFRLevel | null;
    secondaryProbability: number | null;
    confidence: number;
    confidenceLabel: string;
    fullDistribution: CEFRProbabilities;
    displayText: string;
    totalTimeSeconds: number;
    correctCount: number;
    levelDescription: {
        name: string;
        description: string;
    };
    listeningCefrLevel?: CEFRLevel | null;
    speakingCefrLevel?: CEFRLevel | null;
    compositeCefrLevel?: CEFRLevel | null;
    dimensionScores?: Record<string, number> | null;
    speakingSkipped?: boolean;
    questionBreakdown?: QuestionBreakdown[];
}

export interface DictationQuestion {
    id: string;
    audioUrl: string;
    hints?: string[];
    maxReplays: number;
    cefrLevel: CEFRLevel;
    questionIndex: number;
    wordCount: number;
    wordLengths: number[];
    wordPunctuation?: { before: string; after: string; isPunctuationOnly?: boolean }[];
    expectedText?: string;
}

export interface DictationScoreResult {
    score: number;
    totalWords: number;
    correctWords: number;
    closeWords: number;
    missedWords: number;
    cefrEstimate: CEFRLevel;
}

export interface SpeakingQuestion {
    id: string;
    audioUrl: string;
    referenceText: string;
    taskType: string;
    cefrLevel: CEFRLevel;
    questionIndex: number;
}

export interface SpeakingScoreResult {
    score: number;
    accuracy: number;
    fluency: number;
    completeness: number;
    prosody: number;
    cefrEstimate: CEFRLevel;
}

export interface StartTestResponse {
    sessionId: string;
    backgroundQuestions: BackgroundQuestion[];
}

export interface SubmitBackgroundResponse {
    priorProbabilities: CEFRProbabilities;
    firstQuestion: TestQuestion;
}

export interface SubmitAnswerResponse {
    isCorrect: boolean;
    skipped?: boolean;
    currentProbabilities: CEFRProbabilities;
    questionIndex: number;
    nextQuestion: TestQuestion | null;
    requiresLogin?: boolean;
    preview?: {
        message: string;
        teaser: string;
    };
    result?: TestResult;
    phaseComplete?: 'core';
    nextPhase?: 'listening_dictation';
}

export interface QuestionKnowledgePoint {
    term: string;
    cefrLevel: CEFRLevel;
    category: string;
    relevance: 'primary' | 'secondary';
    masteryDescriptor: Record<string, string>;
    weaknessDescriptor: Record<string, string>;
}

export interface QuestionBreakdown {
    index: number;
    questionType: string;
    cefrLevel: CEFRLevel;
    stem: string;
    userAnswer?: string;
    correctAnswer?: string;
    isCorrect: boolean;
    knowledgePoints: QuestionKnowledgePoint[];
}

export interface CourseRecommendation {
    courseId: string;
    title: string;
    titles: Record<string, string> | null;
    thumbnailUrl: string | null;
    cefrLevel: string | null;
    ratingAverage: number;
    enrollmentCount: number;
    creatorName: string;
    creatorId: string;
    matchScore: number;
    topicMatch: boolean;
    matchedKnowledgePoints: Array<{
        term: string;
        cefrLevel: CEFRLevel;
        category: string;
        weaknessDescriptor: Record<string, string>;
    }>;
}

export interface RecommendationsResponse {
    recommendations: CourseRecommendation[];
    weakKnowledgePoints: Array<{
        term: string;
        cefrLevel: CEFRLevel;
        category: string;
        weaknessDescriptor: Record<string, string>;
    }>;
}

export interface TestResultResponse {
    sessionId: string;
    primaryLevel: CEFRLevel;
    primaryProbability: number;
    secondaryLevel: CEFRLevel | null;
    secondaryProbability: number | null;
    confidence: number;
    confidenceLabel: string;
    fullDistribution: CEFRProbabilities;
    displayText: string;
    totalTimeSeconds: number;
    correctCount: number;
    totalQuestions: number;
    levelDescription: {
        name: string;
        description: string;
    };
    questionBreakdown?: QuestionBreakdown[];
    createdAt: string;
}

export interface TestHistoryEntry {
    sessionId: string;
    primaryLevel: CEFRLevel;
    confidence: number;
    confidenceLabel: string;
    displayText: string;
    correctCount: number;
    totalTimeSeconds: number;
    createdAt: string;
}

export interface TestHistoryResponse {
    results: TestHistoryEntry[];
    total: number;
}

export interface PlacementTestStatus {
    hasCompleted: boolean;
    latestLevel: CEFRLevel | null;
    completedAt: string | null;
}

// API Functions — all requests go through baseApiClient which auto-refreshes JWT
export const placementTestService = {
    async startTest(): Promise<StartTestResponse> {
        const response = await baseApiClient.post('/placement-test/start', {});
        return response.data;
    },

    async submitBackground(
        sessionId: string,
        answers: BackgroundAnswer[]
    ): Promise<SubmitBackgroundResponse> {
        const response = await baseApiClient.post(
            `/placement-test/${sessionId}/background`,
            { answers }
        );
        return response.data;
    },

    async submitAnswer(
        sessionId: string,
        questionId: string,
        selectedAnswer: number,
        responseTimeMs: number,
        skipped?: boolean,
    ): Promise<SubmitAnswerResponse> {
        const body: Record<string, unknown> = {
            questionId,
            selectedAnswer,
            responseTimeMs,
        };
        if (skipped) {
            body.skipped = true;
        }
        const response = await baseApiClient.post(
            `/placement-test/${sessionId}/answer`,
            body,
        );
        return response.data;
    },

    async bindSession(sessionId: string): Promise<{ success: boolean }> {
        const response = await baseApiClient.post(
            '/placement-test/bind',
            { sessionId },
        );
        return response.data;
    },

    async getResult(sessionId: string): Promise<TestResultResponse> {
        const response = await baseApiClient.get(
            `/placement-test/${sessionId}/result`,
        );
        return response.data;
    },

    async getHistory(): Promise<TestHistoryResponse> {
        const response = await baseApiClient.get(
            '/placement-test/my-results',
        );
        return response.data;
    },

    async getStatus(): Promise<PlacementTestStatus> {
        const response = await baseApiClient.get('/placement-test/status');
        return response.data;
    },

    async getListeningDictation(sessionId: string, mode?: 'keyboard' | 'letter-picker'): Promise<{
        questions: DictationQuestion[];
        totalQuestions: number;
        currentIndex: number;
    }> {
        const params = mode === 'letter-picker' ? '?mode=letter-picker' : '';
        const response = await baseApiClient.get(
            `/placement-test/${sessionId}/listening-dictation${params}`,
        );
        return response.data;
    },

    async submitDictation(
        sessionId: string,
        questionId: string,
        userText: string,
        responseTimeMs: number,
    ): Promise<{
        score: DictationScoreResult;
        questionIndex: number;
        isComplete: boolean;
        nextPhase?: 'speaking';
    }> {
        const response = await baseApiClient.post(
            `/placement-test/${sessionId}/listening-dictation`,
            { questionId, userText, responseTimeMs },
        );
        return response.data;
    },

    async getSpeakingQuestion(sessionId: string): Promise<{
        questions: SpeakingQuestion[];
        totalQuestions: number;
        currentIndex: number;
    }> {
        const response = await baseApiClient.get(
            `/placement-test/${sessionId}/speaking`,
        );
        return response.data;
    },

    async submitSpeaking(
        sessionId: string,
        questionId: string,
        audioBlob: Blob,
    ): Promise<{
        score: SpeakingScoreResult;
        questionIndex: number;
        isComplete: boolean;
    }> {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.wav');
        formData.append('questionId', questionId);

        const response = await baseApiClient.post(
            `/placement-test/${sessionId}/speaking`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
    },

    async skipSpeaking(sessionId: string): Promise<{ success: boolean }> {
        const response = await baseApiClient.post(
            `/placement-test/${sessionId}/skip-speaking`,
        );
        return response.data;
    },

    async getFinalResult(sessionId: string): Promise<{
        result: TestResult;
    }> {
        const response = await baseApiClient.get(
            `/placement-test/${sessionId}/final-result`,
        );
        return response.data;
    },

    async getRecommendations(sessionId: string): Promise<RecommendationsResponse> {
        const response = await baseApiClient.get(
            `/placement-test/${sessionId}/recommendations`,
        );
        return response.data;
    },
};

export default placementTestService;
